import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Stack, Typography } from '@mui/material'
import type { ProjectSettingsDTO } from '@api/fastAPI.schemas'
import {
  getGetProjectSettingsQueryKey,
  useGetProjectSettings,
  useUpdateProjectSettings,
} from '@api/project-settings/project-settings'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { Form2, Select } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import ManagerSection from '../ManagerSection'
import {
  configurationSettings,
  FOLLOW_ORGANISATION,
  fromProjectSettingValue,
  ProjectSettingValue,
  SettingKey,
  toProjectSettingValue,
} from './settings'

type FormValues = Record<SettingKey, ProjectSettingValue>

const yesNo = (value?: boolean): string => (value ? 'Ja' : 'Nein')

/**
 * Konfiguration (project Systemmanager): per setting, follow the organisation default or override
 * it for this project. Each selection saves on change.
 */
const ProjectSettingsSection = (): ReactElement => {
  const { projectId } = useParams()
  const projectGuid = projectId as string
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { data: settings, isLoading } = useGetProjectSettings<ProjectSettingsDTO, AxiosError>(
    projectGuid,
    {
      query: {
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Konfiguration'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const { mutate: updateSettings } = useUpdateProjectSettings<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Konfiguration speichern',
        description: 'Ändert die Konfiguration des Projekts',
      },
      onSuccess: data => {
        queryClient.setQueryData(getGetProjectSettingsQueryKey(projectGuid), data)
        enqueueSnackbar('Konfiguration gespeichert', { variant: 'success' })
      },
      onError: async error => {
        await queryClient.invalidateQueries(getGetProjectSettingsQueryKey(projectGuid))
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Konfiguration'), {
          variant: 'error',
        })
      },
    },
  })

  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        configurationSettings.map(({ key }) => [
          key,
          toProjectSettingValue(settings?.overrides[key]),
        ]),
      ) as FormValues,
    [settings],
  )

  const onSubmit = useCallback(
    (values: FormValues) =>
      updateSettings({
        projectGuid,
        data: Object.fromEntries(
          configurationSettings.map(({ key }) => [key, fromProjectSettingValue(values[key])]),
        ),
      }),
    [updateSettings, projectGuid],
  )

  return (
    <ManagerSection title="Konfiguration" data-cy="project-settings">
      {isLoading || !settings ? (
        <LoadingIndicator />
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Standardmäßig gilt die Konfiguration der Organisation. Hier kann sie für dieses Projekt
            überschrieben werden.
          </Typography>
          <Form2<FormValues>
            id="project-settings-form"
            defaultValues={defaultValues}
            reinitialize
            submitOnChange
            onSubmit={onSubmit}
          >
            <Stack spacing={2}>
              {configurationSettings.map(setting => (
                <Select<FormValues>
                  key={setting.key}
                  name={setting.key}
                  label={setting.label}
                  size="small"
                  options={[
                    {
                      value: FOLLOW_ORGANISATION,
                      label: `Wie Organisation (${yesNo(
                        settings.organisation_defaults[setting.key],
                      )})`,
                    },
                    { value: 'true', label: 'Ja' },
                    { value: 'false', label: 'Nein' },
                  ]}
                  data-cy={`select-project-setting-${setting.key}`}
                />
              ))}
            </Stack>
          </Form2>
        </Stack>
      )}
    </ManagerSection>
  )
}

export default ProjectSettingsSection
